import React from "react";

import Image from "next/image";
import CornerOffIcon from "public/corner-50-off.svg";
import ElevenYearsIcon from "public/icons/eleven-years-icon.svg";

import { css, styled, SvgIcon } from "@mui/material";

import { Button } from "@/components/form/Button/Button";
import { CLOUDFRONT_FONTS_LINK } from "@/constants/links";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import { ConditionalRender } from "@/lib/conditional-render/ConditionalRender";
import {
  getIsNewLeftbarDesign,
  isFeatureFlagEnabled,
} from "@/lib/feature-flags";
import { clsxm } from "@/utils/tailwindMerge";
import { makeUrl } from "@/utils/url-utils/urlMaker";

type PropTypes = {
  isMainBlockScrolledDown?: boolean;
  isReplenished?: boolean;
  onClick: () => void;
};

export const OneTimeOfferBanner: React.FC<PropTypes> = ({
  isMainBlockScrolledDown,
  onClick,
  isReplenished,
}) => {
  const isNewLeftbarDesign = getIsNewLeftbarDesign();
  const { isAuthenticated } = useAuthContext();

  if (isAuthenticated && isReplenished) {
    return null;
  }

  return (
    <div
      className="flex flex-col gap-4"
      data-testid={`oneTimeOffer${!isMainBlockScrolledDown ? "" : " scrolled"}`}
    >
      {isNewLeftbarDesign ? (
        <>
          <a
            onClick={onClick}
            style={{ cursor: "pointer" }}
            className={clsxm(
              "h-[298px] relative text-white transition-all duration-500",
              isMainBlockScrolledDown && "h-[130px]",
            )}
          >
            <Image
              src={CornerOffIcon}
              alt="50% off"
              className={clsxm(
                "absolute z-[5]",
                isMainBlockScrolledDown
                  ? "w-[170px] h-[65px] -top-[0px] -left-[9px]"
                  : "w-[190px] h-[90px] -top-[5px]",
              )}
            />

            <Image
              fill
              className="relative rounded-lg"
              style={{ objectFit: "cover", objectPosition: "top" }}
              src={makeUrl(CLOUDFRONT_FONTS_LINK, "img/one-time-offer.jpg")}
              unoptimized
              priority
              alt="One time offer"
              loading="eager"
            />

            <div className="relative p-3 flex flex-col justify-between h-full">
              <span
                className={clsxm(
                  "font-bold",
                  "text-xl",
                  "opacity-100 max-h-0 mt-20",
                  isMainBlockScrolledDown &&
                    "opacity-100 transition-all duration-500 max-h-96 mt-6",
                )}
              >
                ONE TIME OFFER
              </span>

              <div className="text-base font-bold text-center flex flex-col">
                <div
                  className={clsxm(
                    "opacity-0 transition-all duration-1000 max-h-0",
                    !isMainBlockScrolledDown && "opacity-100 max-h-96",
                  )}
                >
                  {!isMainBlockScrolledDown && (
                    <>
                      <p>Test membership</p>
                      <p>of our site only for</p>
                      <div className="flex flex-col items-center">
                        <div className="flex items-baseline justify-center space-x-3">
                          <span className="text-1xl line-through opacity-80">
                            $9.99
                          </span>
                          <PriceSpan className="text-5xl">4.99</PriceSpan>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <Button
                  variant="primary"
                  onHoverVariant="gradient-flip"
                  aria-label="Buy now"
                >
                  Buy now!
                </Button>
              </div>
            </div>
          </a>
          <ConditionalRender
            on={!isFeatureFlagEnabled("LEFTBAR_ELEVEN_YEARS_ICON_HIDDEN")}
          >
            <SvgIcon
              data-testid="trustedPartner"
              sx={{ width: 200, height: "100%" }}
              viewBox="0 0 210 60"
            >
              <ElevenYearsIcon />
            </SvgIcon>
          </ConditionalRender>
        </>
      ) : (
        <>
          <div
            className={clsxm(
              "h-[298px] relative text-white transition-all duration-500",
              isMainBlockScrolledDown && "h-[110px]",
            )}
          >
            <Image
              fill
              className="relative rounded-lg"
              style={{ objectFit: "cover", objectPosition: "top" }}
              src={makeUrl(CLOUDFRONT_FONTS_LINK, "img/one-time-offer.jpg")}
              unoptimized
              priority
              alt="One time offer"
              loading="eager"
            />
            <div className="relative p-3 flex flex-col justify-between h-full">
              <span className="font-bold">ONE TIME OFFER</span>
              <div className="text-base font-bold text-center flex flex-col">
                <div
                  className={clsxm(
                    "opacity-0 transition-all duration-1000 max-h-0",
                    !isMainBlockScrolledDown && "opacity-100 max-h-96",
                  )}
                >
                  {!isMainBlockScrolledDown ? (
                    <>
                      <p>Test membership</p>
                      <p>of our site only for</p>
                      <div>
                        <PriceSpan className="text-5xl">4.99</PriceSpan>
                      </div>
                    </>
                  ) : null}
                </div>
                <Button
                  variant="primary"
                  onHoverVariant="gradient-flip"
                  onClick={onClick}
                  aria-label="Buy now"
                >
                  Buy now!
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const PriceSpan = styled("strong")(() => {
  return css`
    position: relative;
    font-weight: 700;
    ::before {
      font-size: 20px;
      font-weight: 700;
      content: "$";
      position: absolute;
      top: 3px;
      left: -11px;
    }
  `;
});
